import Link from 'next/link'

/// Shared presentational primitives in the SeaFi Treasury visual language. No web3/client hooks, so
/// they compose anywhere. Every interactive surface layers the shared `.rng-*` classes from
/// seafi-treasury.scss with the `FOCUS` ring so the app reads as one system.

/* Consistent keyboard focus ring — the one accent, reserved for focus. */
export const FOCUS =
  'outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--paper)]'

/* Ghost pill link — the one style for a standalone secondary action (e.g. "Browse vaults ↗").
   `fill` swaps the outline for a solid white surface with no contour. */
export function PillLink({
  href,
  external = false,
  fill = false,
  children,
}: {
  href: string
  external?: boolean
  fill?: boolean
  children: React.ReactNode
}) {
  const inner = (
    <>
      {children}
      <svg
        aria-hidden="true"
        fill="none"
        height="15"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.6"
        viewBox="0 0 24 24"
        width="15"
      >
        <path d="M7 17 17 7M9 7h8v8" />
      </svg>
    </>
  )
  const className = `rng-btn ${fill ? '' : 'rng-btn--ghost'} ${FOCUS}`
  const style = {
    width: 'auto' as const,
    minHeight: 40,
    padding: '8px 20px',
    fontWeight: 400,
    ...(fill ? { background: 'var(--surface)', border: 'none', color: 'var(--secondary)' } : {}),
  }
  return external ? (
    <a className={className} href={href} rel="noopener noreferrer" style={style} target="_blank">
      {inner}
    </a>
  ) : (
    <Link className={className} href={href} style={style}>
      {inner}
    </Link>
  )
}

export function usd(value: number | undefined, maxFractionDigits = 2): string {
  if (value === undefined || !Number.isFinite(value)) return '—'
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: maxFractionDigits,
  })
}

export function fmtAmount(value: string | number | undefined, maxFractionDigits = 6): string {
  if (value === undefined) return '—'
  const n = typeof value === 'number' ? value : parseFloat(value)
  if (!Number.isFinite(n)) return '—'
  return n.toLocaleString('en-US', {
    minimumFractionDigits: Math.min(2, maxFractionDigits),
    maximumFractionDigits: maxFractionDigits,
  })
}

/* AssetMark — a circular token mark. Pass `glyph` for a text symbol or `src` for an image. */
export function AssetMark({
  glyph,
  src,
  alt = '',
  size = 34,
  color,
  border,
}: {
  glyph?: string
  src?: string
  alt?: string
  size?: number
  color?: string
  border?: string
}) {
  return (
    <span
      aria-hidden={src ? undefined : 'true'}
      className="grid flex-none place-items-center overflow-hidden rounded-full"
      style={{
        width: size,
        height: size,
        border: border ?? '1px solid var(--rule-light)',
        color: color ?? 'var(--ink)',
        fontFamily: 'var(--font-nav)',
        fontSize: size * 0.42,
        fontWeight: 400,
      }}
    >
      {src ? (
        <img alt={alt} height={size} src={src} style={{ objectFit: 'cover' }} width={size} />
      ) : (
        (glyph ?? '◎')
      )}
    </span>
  )
}

export function Panel({
  children,
  className = '',
  href,
}: {
  children: React.ReactNode
  className?: string
  href?: string
}) {
  if (href) {
    return (
      <Link
        className={`rng-panel is-link no-underline transition-shadow ${FOCUS} ${className}`}
        href={href}
        style={{ color: 'inherit' }}
      >
        {children}
      </Link>
    )
  }
  return <section className={`rng-panel ${className}`}>{children}</section>
}

export function PageTitle({
  title,
  subtitle,
  right,
}: {
  title: React.ReactNode
  subtitle?: React.ReactNode
  right?: React.ReactNode
}) {
  return (
    <div className="mb-8 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between sm:gap-8">
      <div>
        <h1
          style={{
            fontSize: 'clamp(32px, 3.4vw, 46px)',
            fontWeight: 400,
            lineHeight: 1.02,
            letterSpacing: 0,
          }}
        >
          {title}
        </h1>
        {subtitle && (
          <p className="mt-3" style={{ fontSize: 13, color: 'var(--muted)' }}>
            {subtitle}
          </p>
        )}
      </div>
      {right && <div className="sm:flex-none">{right}</div>}
    </div>
  )
}

export function Label({ children }: { children: React.ReactNode }) {
  return <div className="rng-label">{children}</div>
}

export function ApyBadge({ v, label = 'Target APY' }: { v: number; label?: string }) {
  return (
    <div className="flex-none text-right">
      <div className="tabular-nums" style={{ fontSize: 20, fontWeight: 400, letterSpacing: 0 }}>
        {v.toFixed(2)}%
      </div>
      <div className="rng-labelcaps">{label}</div>
    </div>
  )
}

/* Button — ink is the one action; ghost is the quiet outline. Forwards native props. */
export function Button({
  variant = 'ink',
  className = '',
  type = 'button',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'ink' | 'ghost' }) {
  return <button className={`rng-btn rng-btn--${variant} ${className}`} type={type} {...props} />
}

/* Field — the amount input shell; the one place the accent appears, on focus-within. */
export function Field({
  prefix,
  suffix,
  className = '',
  ...props
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, 'prefix' | 'suffix'> & {
  prefix?: React.ReactNode
  suffix?: React.ReactNode
}) {
  return (
    <div className={`rng-field flex items-center gap-2 ${className}`}>
      {prefix}
      <input className="rng-input" {...props} />
      {suffix}
    </div>
  )
}

/* Tabs — weight + underline, never colour. */
export function Tabs({ children, className = '', ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={`rng-tabs ${className}`} {...props}>
      {children}
    </div>
  )
}

export function Tab({
  active = false,
  className = '',
  type = 'button',
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }) {
  return (
    <button
      aria-selected={active}
      className={`rng-tab ${active ? 'is-active' : ''} ${FOCUS} ${className}`}
      type={type}
      {...props}
    >
      {children}
    </button>
  )
}

/* Chip — sober state marker. `tone` maps to the shared chip variants; `fill` swaps the outline for
   a solid surface fill (a white label with no contour). */
export function Chip({
  tone = 'neutral',
  dot = false,
  fill = false,
  ink = false,
  children,
}: {
  tone?: 'neutral' | 'warn' | 'pos' | 'crit'
  dot?: boolean
  fill?: boolean
  ink?: boolean
  children: React.ReactNode
}) {
  const cls =
    tone === 'warn'
      ? 'rng-chip rng-chip--warn'
      : tone === 'pos'
        ? 'rng-chip rng-chip--pos'
        : 'rng-chip'
  const critStyle =
    tone === 'crit'
      ? { color: 'var(--crit)', borderColor: 'color-mix(in srgb, var(--crit) 30%, transparent)' }
      : undefined
  const fillStyle = fill ? { background: 'var(--surface)', borderColor: 'transparent' } : undefined
  const inkStyle = ink
    ? { background: 'var(--ink)', borderColor: 'transparent', color: 'var(--paper)' }
    : undefined
  const dotColor =
    tone === 'warn'
      ? 'var(--warn)'
      : tone === 'pos'
        ? 'var(--pos)'
        : tone === 'crit'
          ? 'var(--crit)'
          : 'var(--faint)'
  return (
    <span className={cls} style={{ ...critStyle, ...fillStyle, ...inkStyle }}>
      {dot && <span aria-hidden="true" className="rng-dot" style={{ background: dotColor }} />}
      {children}
    </span>
  )
}

/* StatRow — a labelled figure: caps-ish label, tabular value, optional sub. */
export function StatRow({
  label,
  value,
  sub,
  size = 16,
  accent = false,
}: {
  label: React.ReactNode
  value: React.ReactNode
  sub?: React.ReactNode
  size?: number
  accent?: boolean
}) {
  return (
    <div>
      <Label>{label}</Label>
      <div
        className="tabular-nums"
        style={{
          fontSize: size,
          fontWeight: 400,
          marginTop: 4,
          letterSpacing: 0,
          color: accent ? 'var(--accent-strong)' : undefined,
        }}
      >
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 4 }}>{sub}</div>}
    </div>
  )
}

/* StatusStrip — hairline-separated cells on a quiet ground. Children are the cells. */
export function StatusStrip({
  children,
  className = '',
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={`rng-strip ${className}`}>
      <div className="rng-strip-inner">{children}</div>
    </div>
  )
}

/* SectionHead — a section title with optional right-aligned meta/link. */
export function SectionHead({ title, right }: { title: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="mb-4 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <h2 style={{ fontSize: 16, fontWeight: 400, letterSpacing: 0 }}>{title}</h2>
      {right && (
        <div className="tabular-nums" style={{ fontSize: 12, color: 'var(--muted)' }}>
          {right}
        </div>
      )}
    </div>
  )
}
